import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { api } from '@/modules/common/api/index.ts';
import { useGetRegisterIdParam } from '@/modules/common/hooks/useGetRegisterIdParam.ts';
import {
  GetCommunicationPreferenceChartResponse,
  GetIssuedCapitalChartResponse,
  GetIssuedCapitalChartV1SrChartsIssuerIssuedCapitalGetParams,
  GetIssuedCapitalOverTimeV1SrChartsIssuerIssuedCapitalOverTimeGetParams,
  GetIssuedCapitalOvertimeChartResponse,
  GetPaymentElectionChartResponse,
  GetPendingApplicationSummaryChartResponse,
  GetPendingApplicationSummarySectionResponse,
  GetPendingApplicationSummarySectionV1SrSectionsIssuerPendingApplicationSummaryGetParams,
  GetPendingApplicationSummaryV1SrChartsIssuerPendingApplicationSummaryGetParams,
} from 'api';

export const ISSUER_COMMUNICATION_PREFERENCES_QUERY_KEY =
  'vestra-sr/issuer-communication-preferences';
export const ISSUER_PAYMENT_ELECTION_QUERY_KEY = 'vestra-sr/issuer-payment-election';
export const ISSUED_CAPITAL_KEY = 'vestra/issuer-issued-capital';
export const ISSUER_PENDING_APPLICATION_SUMMARY_SECTION_KEY =
  'vestra/issuer-pending-application-summary-section';
export const ISSUER_PENDING_APPLICATION_SUMMARY_CHART_KEY =
  'vestra/issuer-pending-application-summary-chart';
export const ISSUER_ISSUED_CAPITAL_OVER_TIME_CHART_KEY =
  'vestra/issuer-issued-capital-over-time-chart';

export const useGetCommunicationPreferencesQuery = (disabled = false) => {
  return useQuery<GetCommunicationPreferenceChartResponse>({
    queryKey: [ISSUER_COMMUNICATION_PREFERENCES_QUERY_KEY],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response =
        await api.v1.getCommunicationPreferenceChartV1SrChartsIssuerCommunicationPreferenceGet();
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useGetPaymentElectionQuery = (disabled = false) => {
  const registerId = useGetRegisterIdParam();
  return useQuery<GetPaymentElectionChartResponse>({
    queryKey: [ISSUER_PAYMENT_ELECTION_QUERY_KEY, registerId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getPaymentElectionChartV1SrChartsIssuerPaymentElectionGet();
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useIssuerIssuedCapitalQuery = (
  payload: GetIssuedCapitalChartV1SrChartsIssuerIssuedCapitalGetParams,
  disabled = false,
) => {
  const registerId = useGetRegisterIdParam();

  return useQuery<GetIssuedCapitalChartResponse>({
    queryKey: [ISSUED_CAPITAL_KEY, registerId, JSON.stringify(payload)],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getIssuedCapitalChartV1SrChartsIssuerIssuedCapitalGet(payload);
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useIssuerGetPendingApplicationSummarySectionQuery = (
  payload: GetPendingApplicationSummarySectionV1SrSectionsIssuerPendingApplicationSummaryGetParams,
  disabled = false,
) => {
  const registerId = useGetRegisterIdParam();

  const { date_to, date_from, ...data } = payload;

  return useQuery<GetPendingApplicationSummarySectionResponse>({
    queryKey: [
      ISSUER_PENDING_APPLICATION_SUMMARY_SECTION_KEY,
      registerId,
      JSON.stringify(data),
      date_to ? dayjs(date_to).format('YYYY-MM-DD') : null,
      date_from ? dayjs(date_from).format('YYYY-MM-DD') : null,
    ],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response =
        await api.v1.getPendingApplicationSummarySectionV1SrSectionsIssuerPendingApplicationSummaryGet(
          payload,
        );
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useIssuerGetPendingApplicationSummaryChartQuery = (
  payload: GetPendingApplicationSummaryV1SrChartsIssuerPendingApplicationSummaryGetParams,
  disabled = false,
) => {
  const registerId = useGetRegisterIdParam();

  const { date_to, date_from, ...data } = payload;

  return useQuery<GetPendingApplicationSummaryChartResponse>({
    queryKey: [
      ISSUER_PENDING_APPLICATION_SUMMARY_CHART_KEY,
      registerId,
      JSON.stringify(data),
      date_to ? dayjs(date_to).format('YYYY-MM-DD') : null,
      date_from ? dayjs(date_from).format('YYYY-MM-DD') : null,
    ],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response =
        await api.v1.getPendingApplicationSummaryV1SrChartsIssuerPendingApplicationSummaryGet(
          payload,
        );
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useIssuerGetIssuedCapitalOverTimeQuery = (
  payload: GetIssuedCapitalOverTimeV1SrChartsIssuerIssuedCapitalOverTimeGetParams,
  disabled = false,
) => {
  const registerId = useGetRegisterIdParam();
  const { date_to, date_from, ...data } = payload;
  return useQuery<GetIssuedCapitalOvertimeChartResponse>({
    queryKey: [
      ISSUER_ISSUED_CAPITAL_OVER_TIME_CHART_KEY,
      registerId,
      JSON.stringify(data),
      date_to ? dayjs(date_to).format('YYYY-MM-DD') : null,
      date_from ? dayjs(date_from).format('YYYY-MM-DD') : null,
    ],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response =
        await api.v1.getIssuedCapitalOverTimeV1SrChartsIssuerIssuedCapitalOverTimeGet(payload);
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};
